import { CompactTable } from "@table-library/react-table-library/compact";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";
import { usePagination } from "@table-library/react-table-library/pagination";
import Styles from "../../pages/users/users.module.scss";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiEye, FiFilter, FiSearch, FiCalendar } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { resetAppointmentNotification } from "../../store/notification/notificationActions";
import { getAppointmentList } from "../../store/appointment/appointmentActions";
import { getImage } from "../../utils";
import moment from "moment";
import NotFoundWidget from "./NotFound";
import { TablePagination } from "../global/table-pagination";

const AppointmentWidget = ({
    profile,
    role,
    appointments: appointmentsFromParent,
    /** When true, skip hero header (use on user profile where a parent title exists). */
    embedded = false,
    title = "Appointments",
    subtitle = "Your scheduled lessons and their status",
    searchPlaceholder = "Search by name or date...",
}) => {
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState([]);

    const handleSearch = (event) => {
        setSearch(event.target.value);
    };
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(resetAppointmentNotification());
        if (Array.isArray(appointmentsFromParent)) {
            setNodes(appointmentsFromParent);
            return;
        }
        dispatch(getAppointmentList({ profile, role }))
            .unwrap()
            .then((v) => setNodes(v));
    }, [dispatch, profile, role, appointmentsFromParent]);

    const [nodes, setNodes] = useState([]);

    const AppointmentStatus = ({ status }) => {
        if (status === -1) {
            return <span className="rounded bg-nt-blue px-2 py-1 text-white">Pending</span>;
        }
        if (status === 0) {
            return <span className="rounded bg-nt-blue px-2 py-1 text-white">Pending Payment</span>;
        }
        if (status === 1) {
            return <span className="rounded bg-green px-2 py-1 text-white">Done</span>;
        }
        if (status === 2) {
            return <span className="rounded bg-red px-2 py-1 text-white">Canceled</span>;
        }
        if (status === 3) {
            return <span className="rounded bg-green/70 px-2 py-1 text-white">Under Review</span>;
        }
        if (status === 4) {
            return <span className="rounded bg-red/70 px-2 py-1 text-white">Payment Rejected</span>;
        }
        return null;
    };

    const COLUMNS = [
        {
            label: "Avatar",
            class: "avatar",
            renderCell: (item) => (
                <img className={Styles.avatar} src={getImage(item.avatar, "AVATAR")} alt="" />
            ),
            pinLeft: true,
        },
        {
            label: "Name",
            renderCell: (item) => (
                <p className="font-medium capitalize text-nt-blue">{(item.fullname || "").toString()}</p>
            ),
            pinLeft: true,
        },
        {
            label: "Price",
            renderCell: (item) => (item.price ? `${item.price} €` : ""),
            pinLeft: true,
        },
        {
            label: "Date",
            renderCell: (item) => moment(item.date).utc().format("YYYY-MM-DD").toString(),
            pinLeft: true,
        },
        {
            label: "Time",
            renderCell: (item) =>
                `${moment(item.date).parseZone().format("HH:mm")} – ${moment(item.date).parseZone().add(50, "minutes").format("HH:mm")}`,
            pinLeft: true,
        },
        {
            label: "Status",
            renderCell: (item) => (
                <div className={Styles.profileList}>
                    <AppointmentStatus status={item.status} />
                </div>
            ),
            pinLeft: true,
        },
        {
            label: "",
            renderCell: (item) => (
                <Link className={Styles.viewBtn} to={`/appointment/detail/${item._id}`}>
                    <FiEye />
                    <span>View</span>
                </Link>
            ),
            pinLeft: true,
        },
    ];

    const theme = useTheme([
        getTheme(),
        {
            HeaderRow: `color: #191D51;`,
            Table: `--data-table-library_grid-template-columns: 150px auto 200px 200px 200px 200px 200px; width:auto;min-width:auto;min-height:auto;`,
        },
    ]);

    let data = { nodes };

    data = {
        nodes: data.nodes.filter(
            (item) =>
                ((item.fullname || "").toString().toLowerCase().includes(search.toLowerCase()) ||
                    (item.date || "").toString().toLowerCase().includes(search.toLowerCase())) &&
                ((filter.length && filter.includes(item.status)) || filter.length === 0)
        ),
    };

    const pagination = usePagination(data, {
        state: {
            page: 0,
            size: 5,
        },
    });

    const pageButtonCount = 2;
    const totalPages = pagination.state.getTotalPages(data.nodes);
    const pages = pagination.state.getPages(data.nodes);

    const toggleFilter = (status) => {
        pagination.fns.onSetPage(0);
        if (filter.includes(status)) setFilter((x) => x.filter((v) => v !== status));
        else setFilter((x) => [...x, status]);
    };

    const filterChip = (status, label) => (
        <button
            key={status}
            type="button"
            onClick={() => toggleFilter(status)}
            className={`cursor-pointer select-none rounded-lg border border-slate-200 px-2.5 py-1 text-sm transition hover:opacity-90 ${
                filter.includes(status) ? "bg-nt-blue text-white" : "bg-white text-nt-blue"
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className={embedded ? "" : Styles.tableContainer}>
            {!embedded && (
                <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <div className="grid h-10 w-10 place-items-center rounded-xl bg-nt-blue/10 text-nt-blue">
                                <FiCalendar size={18} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-nt-blue">{title}</h2>
                                <p className="text-sm text-nt-grey">{subtitle}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-nt-grey">
                    {data.nodes.length} result{data.nodes.length === 1 ? "" : "s"}
                </p>
                <label htmlFor="appointment-widget-search" className="relative w-full sm:w-[300px]">
                    <FiSearch
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-nt-grey"
                        size={16}
                    />
                    <input
                        id="appointment-widget-search"
                        type="text"
                        placeholder={searchPlaceholder}
                        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-nt-blue focus:ring-2 focus:ring-nt-blue/10"
                        value={search}
                        onChange={handleSearch}
                    />
                </label>
            </div>

            <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <FiFilter className="shrink-0 text-nt-blue" size={18} aria-hidden />
                {filterChip(-1, "Pending")}
                {filterChip(0, "Pending Payment")}
                {filterChip(1, "Done")}
                {filterChip(2, "Canceled")}
                {filterChip(3, "Under Review")}
                {filterChip(4, "Payment Rejected")}
            </div>

            <CompactTable
                columns={COLUMNS}
                layout={{ custom: true, horizontalScroll: true }}
                data={data}
                theme={theme}
                pagination={pagination}
            />
            {nodes.length === 0 ? <NotFoundWidget message="No appointment history" /> : null}
            {totalPages > 0 ? (
                <TablePagination
                    pageButtonCount={pageButtonCount}
                    totalPages={totalPages}
                    pagination={pagination}
                    pages={pages}
                />
            ) : null}
        </div>
    );
};

export { AppointmentWidget };
